import { useState } from "react";
import { GoogleLogo, FacebookLogo, WhatsappLogo, Eye } from "@phosphor-icons/react";

/* SnippetPreview — one button + a tiny modal that renders the exact
 * search result / social preview card that Google, WhatsApp and Facebook
 * will show for a given (title, description, url, image) tuple.
 *
 * Drop-in usage:
 *   <SnippetPreview
 *     title={seo.seo_title}
 *     description={seo.seo_description}
 *     url="https://recipefiler.com/pricing"
 *     image={cookbookCoverUrl}
 *     brand="Recipe Filer"
 *   />
 *
 * The renders are visually faithful (fonts, colours, truncation rules,
 * favicon placement) but SIMPLIFIED — no live data or dynamic snippets,
 * just what your metadata will look like once saved.
 */
export default function SnippetPreview({
  title = "",
  description = "",
  url = "https://recipefiler.com/",
  image = null,
  brand = "Recipe Filer",
  dataTestidPrefix = "snippet-preview",
}) {
  const [open, setOpen] = useState(false);

  // Google truncates at ~60 title chars / ~160 description chars.
  const gTitle = trim(title || `${brand} — homepage`, 60);
  const gDesc = trim(description || "No description set.", 160);
  const displayUrl = url
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${dataTestidPrefix}-open`}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-rf-orange hover:underline"
      >
        <Eye size={14} weight="duotone" /> Preview snippet
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-50 bg-rf-ink/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          data-testid={`${dataTestidPrefix}-modal`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-2xl bg-rf-cream rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
          >
            <div className="flex items-start justify-between p-5 border-b border-rf-ink/10 bg-white">
              <div>
                <p className="overline">SEO preview</p>
                <h2 className="font-display font-black text-rf-ink text-xl">
                  How this page will look
                </h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full hover:bg-rf-ink/10 text-lg font-bold"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Google */}
              <section data-testid={`${dataTestidPrefix}-google`}>
                <h3 className="text-xs font-black uppercase tracking-wider text-rf-ink-soft mb-2 inline-flex items-center gap-1.5">
                  <GoogleLogo size={14} weight="fill" /> Google search result
                </h3>
                <div className="rounded-lg bg-white p-4 border border-rf-ink/10 font-sans">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-rf-orange flex items-center justify-center text-white text-[9px] font-black">RF</div>
                    <div className="text-xs">
                      <p className="text-rf-ink font-bold leading-tight">{brand}</p>
                      <p className="text-rf-ink-soft leading-tight">{displayUrl}</p>
                    </div>
                  </div>
                  <p className="text-[#1a0dab] text-lg leading-snug hover:underline cursor-pointer">
                    {gTitle}
                  </p>
                  <p className="text-sm text-[#4d5156] leading-snug mt-1">{gDesc}</p>
                </div>
              </section>

              {/* WhatsApp */}
              <section data-testid={`${dataTestidPrefix}-whatsapp`}>
                <h3 className="text-xs font-black uppercase tracking-wider text-rf-ink-soft mb-2 inline-flex items-center gap-1.5">
                  <WhatsappLogo size={14} weight="fill" /> WhatsApp / iMessage
                </h3>
                <div className="max-w-sm rounded-2xl overflow-hidden bg-[#dcf8c6] p-1 shadow-sm">
                  {image && (
                    <img
                      src={image}
                      alt=""
                      className="w-full h-32 object-cover rounded-xl"
                    />
                  )}
                  <div className="p-2">
                    <p className="text-xs font-bold text-rf-ink leading-tight line-clamp-2">
                      {trim(title || brand, 65)}
                    </p>
                    <p className="text-[11px] text-rf-ink-soft leading-snug line-clamp-2 mt-1">
                      {trim(description || "", 110)}
                    </p>
                    <p className="text-[10px] text-rf-ink-soft uppercase mt-1">{displayUrl}</p>
                  </div>
                </div>
              </section>

              {/* Facebook */}
              <section data-testid={`${dataTestidPrefix}-facebook`}>
                <h3 className="text-xs font-black uppercase tracking-wider text-rf-ink-soft mb-2 inline-flex items-center gap-1.5">
                  <FacebookLogo size={14} weight="fill" /> Facebook share card
                </h3>
                <div className="rounded-lg bg-white border border-rf-ink/10 overflow-hidden max-w-lg">
                  {image ? (
                    <img src={image} alt="" className="w-full h-48 object-cover" />
                  ) : (
                    <div className="w-full h-48 bg-rf-amber/30 flex items-center justify-center text-rf-ink-soft text-xs">
                      No preview image set — will fall back to site default
                    </div>
                  )}
                  <div className="p-3 bg-[#f0f2f5]">
                    <p className="text-[10px] text-rf-ink-soft uppercase leading-tight">{displayUrl}</p>
                    <p className="text-sm font-black text-rf-ink leading-snug mt-0.5 line-clamp-2">
                      {trim(title || brand, 90)}
                    </p>
                    <p className="text-xs text-rf-ink-soft leading-snug mt-0.5 line-clamp-2">
                      {trim(description || "", 130)}
                    </p>
                  </div>
                </div>
              </section>

              <p className="text-xs text-rf-ink-soft italic text-center leading-relaxed">
                Rendered client-side from your current form values — updates the moment you type. Actual Google indexing can take a few days to reflect changes.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function trim(s, n) {
  if (!s) return s;
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trim() + "…";
}
